<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { Dialog, DialogPanel, Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems, } from '@headlessui/vue'
import { computed, inject, onMounted, ref } from 'vue'
import VueDatePicker from '@vuepic/vue-datepicker';
import PureInput from '@/Components/Pure/PureInput.vue'
import { DatePicker, InputNumber } from 'primevue'
import { aikuLocaleStructure } from '@/Composables/useLocaleStructure'
import { routeType } from '@/types/route'
import { debounce } from 'lodash'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import Chart from 'primevue/chart'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faInfoCircle } from '@fal'
import { faSyncAlt } from '@fas'
import { library } from '@fortawesome/fontawesome-svg-core'
import { notify } from '@kyvg/vue3-notification'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import TableFeeAdditional from '@/Components/Tables/TableFeeAdditional.vue'
library.add(faInfoCircle, faSyncAlt)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    calculating: {
        totalSalesOrders: number
        totalFeeSalaries: number
        totalFeeInks: number
        totalFeeOthers: number
        totalProfit: number
        totalAmountOrdersByCash: number
        totalSalesOrdersByTransfer: number
        feeAdditionalOutcome: number
        feeAdditionalIncome: number
        invoiceCashPaidOff: number  // Invoice cash yang sudah lunas
    }
    update_route: routeType
    is_profit_exist: boolean
    profit_graph: {}
    fee_additional: {}[]
}>()


const ymSince = ref({
    month: parseInt((route().params?.since)?.substring(4, 6), 10) || (new Date().getMonth() + 1),
    year: parseInt((route().params?.since)?.substring(0, 4), 10) || new Date().getFullYear()
});

const ymUntil = ref({
    month: parseInt((route().params?.until)?.substring(4, 6), 10) || null,
    year: parseInt((route().params?.until)?.substring(0, 4), 10) || null
});

const isLoading = ref('')
const onSave = (key: string, value: number) => {
    if (props.is_profit_exist) {
        router[props.update_route.method](
            route(props.update_route.name, props.update_route.parameters),
            {
                [key]: value
            },
            {
                preserveScroll: true,
                preserveState: true,
                onStart: () => {
                    isLoading.value = key
                },
                onError: (e) => {
                    console.log('xxxxxxxx', e)
                },
                onFinish: () => {
                    isLoading.value = ''
                },
                onSuccess: () => {
                    // form.reset('password')
                }
            }
        )
    } else {
        notify({
            title: 'Terjadi kesalahan',
            text: 'Data belum tersedia, klik generate sales dulu',
            type: 'error',
        })
    }
    
}

// Section: change date in datepicker
const onChangeDate = (modelValue: typeof ymUntil.value, paramKey: string, month_year?: typeof ymSince.value) => {
    if(!month_year) {
        router.reload(
            {
                data: { [paramKey]: undefined },  // Sent to url parameter (?tab=showcase, ?tab=menu)
                xxonly: ['calculating', 'pageHead'],  // Only reload the props with dynamic name tabSlug (i.e props.showcase, props.menu)
                onSuccess: () => {
                    modelValue.month = null,
                    modelValue.year = null
                },
                onError: (e) => {
                    // console.log('eeerr', e)
                    notify({
                        title: 'Sepertinya ada kesalahan',
                        text: 'Gagal mengubah periode',
                        type: 'error',
                    })
                }
            }
        )
    } else {
        router.reload(
            {
                data: { [paramKey]: `${month_year.year}${(month_year.month + 1).toString().padStart(2, '0')}` },  // Sent to url parameter (?tab=showcase, ?tab=menu)
                xxonly: ['calculating', 'pageHead'],  // Only reload the props with dynamic name tabSlug (i.e props.showcase, props.menu)
                onSuccess: () => {
                    modelValue.month = Number((month_year.month + 1).toString().padStart(2, '0')),
                    modelValue.year = month_year.year
                },
                onError: (e) => {
                    // console.log('eeerr', e)
                    notify({
                        title: 'Sepertinya ada kesalahan',
                        text: 'Gagal mengubah periode',
                        type: 'error',
                    })
                }
            }
        )
    }
}


const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');

    return {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: textColor
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            },
            y: {
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            }
        }
    };
}
const isChartLoaded = ref(false)
const chartOptions = ref<typeof setChartOptions | null>(null)
onMounted(() => {
    chartOptions.value = setChartOptions()
})

const documentStyle = getComputedStyle(document.documentElement);

const cashPercentage = computed(() => {
    return Number(props.calculating.totalAmountOrdersByCash) ? ((Number(props.calculating.totalAmountOrdersByCash) / Number(props.calculating.totalSalesOrders)) * 100) : 0
})
const transferPercentage = computed(() => {
    return Number(props.calculating.totalSalesOrdersByTransfer) ? ((Number(props.calculating.totalSalesOrdersByTransfer) / Number(props.calculating.totalSalesOrders)) * 100) : 0
})

// Section: total cash on hand
const _feeAdditional = ref<InstanceType<typeof TableFeeAdditional> | null>(null)
const compTotalCashOnHand = computed(() => {
    return Number(_feeAdditional.value?.compCalculatedCashOnHand) + Number(props.calculating.totalAmountOrdersByCash)
})
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />
    <!-- {{ ymSince }} -->
    <!-- <pre>{{ profits }}</pre> -->

    <div class="px-4 mt-8 sm:px-6 lg:px-8 flex md:flex-wrap gap-y-4 gap-x-6 ">
        <div>
            Since <span class="text-gray-400">({{ new Date(ymSince.year, ymSince.month - 1).toLocaleString('id-ID', { month: 'long', year: 'numeric' }) }})</span>
            <VueDatePicker
                :clearable="false"
                autoApply
                :modelValue="{
                    month: ymSince.month - 1,
                    year: ymSince.year
                }"
                @update:modelValue="(e) => (console.log(e), onChangeDate(ymSince, 'since', e))"
                month-picker
            />
        </div>

        <div>
            Until <span v-if="ymUntil.month && ymUntil.year" class="text-gray-400">({{ new Date(ymUntil.year, ymUntil.month - 1).toLocaleString('id-ID', { month: 'long', year: 'numeric' }) }})</span>
            <VueDatePicker
                clearable
                autoApply
                :minDate="new Date(Number(ymSince.year), Number(ymSince.month))"
                :modelValue="ymUntil.month && ymUntil.year ? {
                    month: ymUntil.month ? ymUntil.month - 1 : null,
                    year: ymUntil.year
                } : null"
                @update:modelValue="(e) => (console.log(e), onChangeDate(ymUntil, 'until', e))"
                month-picker
            />
        </div>
    </div>

    <div v-if="!(ymUntil.month && ymUntil.year)" class="mt-6 mx-3 md:mx-8 px-3 py-2 border-l-4 border-gray-500 bg-gray-200 rounded-r">
        Laba diambil dari semua Invoice yang dibuat di bulan <span class="font-bold">{{ new Date(ymSince.year, ymSince.month - 1).toLocaleString('id-ID', { month: 'long', year: 'numeric' }) }}</span>.
        Jika ada order di bulan sebelumnya namun order tersebut masuk di Invoice bulan <span class="font-bold">{{ new Date(ymSince.year, ymSince.month - 1).toLocaleString('id-ID', { month: 'long', year: 'numeric' }) }}</span>, maka akan dihitung juga.
    </div>

    <main class="px-4 py-8 sm:px-6 lg:px-8">
        <!-- Graphic -->
        <div class="w-full grid grid-cols-1 lg:grid-cols-3 gap-x-6 lg:gap-x-12 gap-y-8 lg:mx-0 lg:grid-rows-2">
            
            <!-- Section: Box Invoice -->
            <div class="h-fit w-full px-4 py-4 rounded-md ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg sm:px-6 lg:row-end-2 ">

                <!-- Section: Table -->
                <table class="w-full whitespace-nowrap text-left text-sm/6 ">
                    <colgroup>
                        <col class="w-full" />
                        <!-- <col /> -->
                    </colgroup>

                    <thead class="border-b border-transparent">
                        <tr>
                            <!-- <th scope="col" class="px-0 py-3 font-semibold">Projects</th>
                            <th scope="col" class="py-3 xpl-8 pr-0 xtext-right font-semibold">Price</th> -->
                        </tr>
                    </thead>

                    <tbody>
                        <!-- Penjualan -->
                        <tr class="border-b border-gray-300">
                            <td class="w-full px-0 pb-5 md:pb-5 align-top" colspan="2">
                                <div class="truncate font-medium">
                                    Penjualan dari Order
                                    <FontAwesomeIcon v-tooltip="'Dari Invoice (sudah lunas maupun belum)'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
                                </div>
                                <div class="truncate text-gray-500">
                                    Sudah masuk invoice
                                </div>
                                <div class="relative xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                                    <InputNumber
                                        :maxFractionDigits="0"
                                        mode="currency"
                                        currency="IDR"
                                        disabled
                                        locale="id-ID"
                                        :modelValue="calculating.totalSalesOrders"
                                        :inputStyle="{
                                            width: '100%',
                                            paddingRight: '1rem'
                                        }"
                                    />

                                    <div class="mt-3 truncate text-gray-500">
                                        Perbandingan metode pembayaran dari order
                                    </div>
                                    <div class="mb-1 h-10 w-full bg-gray-300 relative rounded overflow-hidden">
                                        <!-- Transfer -->
                                        <div class="transition-all absolute h-full bg-yellow-500 left-0 flex justify-center items-center overflow-hidden"
                                            v-tooltip="'Transfer: ' + useFormatCurrency(Number(calculating.totalSalesOrdersByTransfer))"
                                            :style="{
                                                width: transferPercentage + '%',
                                            }"
                                        >
                                            {{ transferPercentage.toFixed(1) + '%' }}
                                        </div>

                                        <!-- Cash -->
                                        <div class="transition-all absolute h-full bg-cyan-500 right-0 flex justify-center items-center overflow-hidden"
                                            v-tooltip="'Cash: ' + useFormatCurrency(Number(calculating.totalAmountOrdersByCash))"
                                            :style="{
                                                width: cashPercentage + '%',
                                            }"
                                        >
                                            {{ cashPercentage.toFixed(1) + '%' }}
                                        </div>
                                    </div>

                                    <!-- Mini box: Cash and Transfer -->
                                    <div class="flex justify-between flex-wrap gap-y-1 gap-x-2">
                                        <div class="flex items-center gap-x-2">
                                            <div class="h-5 w-5 bg-yellow-500 rounded-sm" />
                                            <div>Transfer <span class="text-gray-400">({{ useFormatCurrency(Number(calculating.totalSalesOrdersByTransfer)) }})</span></div>
                                        </div>
                                        <div class="flex items-center gap-x-2">
                                            <div class="h-5 w-5 bg-cyan-500 rounded-sm" />
                                            <div>Cash <span class="text-gray-400">({{ useFormatCurrency(Number(calculating.totalAmountOrdersByCash)) }})</span></div>
                                        </div>
                                    </div>
                                    <!-- {{ ((Number(calculating.totalAmountOrdersByCash) / Number(calculating.totalSalesOrders)) * 100) }}

                                    {{ useFormatCurrency(Number(calculating.totalAmountOrdersByCash) + Number(calculating.totalSalesOrdersByTransfer)) }} -->
                                </div>
                            </td>
                            <!-- <td class="xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                            </td> -->
                        </tr>

                        <!-- Gaji karyawan & Pembelian tinta -->
                        <tr class="">
                            <td class="w-full px-0 py-5 align-top" colspan="2">
                                <div class="grid grid-cols-2 md:grid-cols-1 gap-6">
                                    <!-- Gaji karyawan -->
                                    <div>
                                        <div class="truncate font-medium text-red-600">Gaji karyawan</div>
                                        <!-- <div class="truncate text-gray-500">Total omset</div> -->
                                        <div class="relative xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                                            <InputNumber
                                                :maxFractionDigits="0"
                                                mode="currency"
                                                currency="IDR"
                                                locale="id-ID"
                                                fluid
                                                :modelValue="calculating.totalFeeSalaries"
                                                @update:modelValue="(e) => (calculating.totalFeeSalaries === e ? false : onSave('fee_salaries', e))"
                                                :disabled="!is_profit_exist || isLoading == 'fee_salaries' || !!ymUntil.month || !!ymUntil.year"
                                                :inputStyle="{
                                                    width: '100%',
                                                    paddingRight: '3rem'
                                                }"
                                            />
                                            <div class="absolute top-1/2 -translate-y-1/2 right-2 text-base">
                                                <LoadingIcon v-if="isLoading == 'fee_salaries'" />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Pembelian Tinta -->
                                    <div class="">
                                        <div class="truncate font-medium text-red-600">Pembelian Tinta</div>
                                    
                                        <div class="relative xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                                            <InputNumber
                                                :maxFractionDigits="0"
                                                mode="currency"
                                                currency="IDR"
                                                locale="id-ID"
                                                fluid
                                                :modelValue="calculating.totalFeeInks"
                                                @update:modelValue="(e) => (calculating.totalFeeInks === e ? false : onSave('fee_inks', e))"
                                                :disabled="!is_profit_exist || isLoading == 'fee_inks' || !!ymUntil.month || !!ymUntil.year"
                                                :inputStyle="{
                                                    width: '100%',
                                                    paddingRight: '3rem'
                                                }"
                                            />
                                    
                                            <div class="absolute top-1/2 -translate-y-1/2 right-2 text-base">
                                                <LoadingIcon v-if="isLoading == 'fee_inks'" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <!-- Lain-lain -->
                        <tr class="">
                            <td class="w-full px-0 pb-5 align-top" colspan="2">
                                <div class="grid grid-cols-2 md:grid-cols-1 gap-6">
                                    <div>
                                        <div class="truncate font-medium text-red-600">Lain-lain</div>
                                        <div class="truncate text-red-600 opacity-60">Bensin, snack, dll.</div>
                                        <div class="relative xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                                            <InputNumber
                                                :maxFractionDigits="0"
                                                mode="currency"
                                                currency="IDR"
                                                locale="id-ID"
                                                fluid
                                                :modelValue="calculating.totalFeeOthers"
                                                @update:modelValue="(e) => (calculating.totalFeeOthers === e ? false : onSave('fee_others', e))"
                                                :disabled="!is_profit_exist || isLoading == 'fee_others' || !!ymUntil.month || !!ymUntil.year"
                                                :inputStyle="{
                                                    width: '100%',
                                                    paddingRight: '3rem'
                                                }"
                                            />
                                    
                                            <div class="absolute top-1/2 -translate-y-1/2 right-2 text-base">
                                                <LoadingIcon v-if="isLoading == 'fee_others'" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>

                        <!-- Section: Fee additional outcome -->
                        <tr class="border-b border-gray-300">
                            <td class="w-full px-0 pb-5 align-top" colspan="2">
                                <div class="grid grid-cols-2 md:grid-cols-1 gap-6">
                                    <div>
                                        <div class="truncate font-medium text-red-600">
                                            Kas pengeluaran
                                        </div>
                                        <div class="truncate text-red-600 opacity-70">Dari kalkulasi arus</div>
                                        <div class="relative xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                                            <InputNumber
                                                :maxFractionDigits="0"
                                                mode="currency"
                                                currency="IDR"
                                                locale="id-ID"
                                                fluid
                                                :modelValue="-(calculating.feeAdditionalOutcome)"
                                                disabled
                                                :inputStyle="{
                                                    width: '100%',
                                                    paddingRight: '3rem',
                                                    background: '#ef444455'
                                                }"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div class="truncate font-medium text-green-600">Kas pemasukan</div>
                                        <div class="truncate text-green-600 opacity-70">Dari kalkulasi arus</div>
                                        <div class="relative xpy-5 xpl-8 pr-0 xtext-right align-top tabular-nums text-gray-700">
                                            <InputNumber
                                                :maxFractionDigits="0"
                                                mode="currency"
                                                currency="IDR"
                                                locale="id-ID"
                                                fluid
                                                :modelValue="calculating.feeAdditionalIncome"
                                                disabled
                                                :inputStyle="{
                                                    width: '100%',
                                                    paddingRight: '3rem',
                                                    background: '#22c55e55'
                                                }"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <tfoot>
                        <!-- Total pendapatan -->
                        <tr class="">
                            <td class="pt-3 pb-0 xpl-8 pr-0 xtext-right text-lg font-semibold tabular-nums">
                                <div scope="row" class="font-semibold">
                                    Total laba
                                    <FontAwesomeIcon v-tooltip="'Kalkulasi dari penjualan order dikurangi gaji karyawan, pembelian tinta, dan lain-lain.'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
                                </div>
                                {{ useFormatCurrency(calculating.totalProfit) }}
                            </td>

                            <td class="pt-3 pb-0 xpl-8 pr-0 text-right text-lg  tabular-nums">
                                <div scope="row" class="">
                                    Total cash
                                    <FontAwesomeIcon v-tooltip="'Cash tersedia dalam sebulan. Kalkulasi cash dari order dan cash dari arus kas.'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
                                </div>
                                <div class="text-lg tabular-nums text-primary"
                                    :class="compTotalCashOnHand >= 0 ? '' : 'text-red-500'"
                                >
                                    {{ useFormatCurrency(compTotalCashOnHand) }}
                                </div>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div
                class="w-full col-span-2 max-w-7xl px-6 md:px-0"
            >
                <div class="border-b border-dashed border-gray-300 pb-6">
                    <div class="h-80 md:h-52 relative border border-gray-300 rounded-lg px-4 py-3">
                        <div v-if="false" class="border-l-4 text-xl border-sky-400 pl-2 pr-4 bg-sky-50 w-fit py-1">
                            Grafik kalkulasi profit perbulan
                            <!-- <FontAwesomeIcon v-tooltip="'Filter maksimal 6 bulan agar '" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' /> -->
                        </div>
                        <div v-show="!isChartLoaded" class="absolute inset-0">
                            <div class="skeleton h-full w-full">
                            </div>
                        </div>
                    
                        <Chart
                            v-show="isChartLoaded"
                            @loaded="isChartLoaded = true"
                            :data="profit_graph"
                            :options="chartOptions"
                            class="h-80 md:h-44 max-h-[20rem]"
                        />
                    </div>
                </div>

                <TableFeeAdditional
                    ref="_feeAdditional"
                    :feeAdditional="fee_additional"
                    :routeUpdate="update_route"
                    :previousMonthYear="{
                        month: ymSince.month,
                        year: ymSince.year
                    }"
                    :calculating
                />
            </div>
        </div>
    </main>
</template>